import { useState } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";

import { StoryItem } from "./StoryItem";
import { DarkStoryPreview } from "./StoryDetails";
import { rebuildMessageText } from "./storydat";
import * as u from "./utility";

function storyChipLabel(story: StoryItem): string {
  if (story.st_cap) return story.st_cap;
  const text = rebuildMessageText(story);
  if (text) return text.length > 40 ? text.slice(0, 40) + "…" : text;
  if (story.SK4) return u.getStoryKey({ SK4: story.SK4 }) || story.SK;
  return story.SK;
}

type JoinLeaderPickerProps = {
  stories: StoryItem[];
  onLeaderChange: (id: string) => void;
  initialLeaderId?: string;
};

export function JoinLeaderPicker({
  stories,
  onLeaderChange,
  initialLeaderId,
}: JoinLeaderPickerProps) {
  const initialIdx = initialLeaderId
    ? Math.max(
        0,
        stories.findIndex((s) => s.SK === initialLeaderId)
      )
    : 0;
  const [leaderIdx, setLeaderIdx] = useState(initialIdx);
  const [previewIdx, setPreviewIdx] = useState(initialIdx);
  const theme = useTheme();

  const handleChipClick = (story: StoryItem) => {
    const idx = stories.indexOf(story);
    setLeaderIdx(idx);
    setPreviewIdx(idx);
    onLeaderChange(story.SK);
  };

  const others = stories.filter((_, i) => i !== leaderIdx);

  return (
    <Stack direction="column" gap={0.75}>
      <Typography
        sx={{
          fontSize: "11px",
          color: "var(--text-secondary)",
          fontWeight: 500,
        }}
      >
        {previewIdx === leaderIdx ? "Leader" : "Preview"}
      </Typography>
      <DarkStoryPreview item={stories[previewIdx]} />
      {others.length > 0 && (
        <>
          <Typography
            sx={{ fontSize: "11px", color: "var(--text-secondary)", mt: 0.5 }}
          >
            Use as leader instead:
          </Typography>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
            {others.map((story) => (
              <Chip
                key={story.SK}
                label={storyChipLabel(story)}
                size="small"
                variant="outlined"
                onClick={() => handleChipClick(story)}
                onMouseEnter={() => setPreviewIdx(stories.indexOf(story))}
                onMouseLeave={() => setPreviewIdx(leaderIdx)}
                sx={{
                  fontSize: "11px",
                  cursor: "pointer",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(138,180,248,0.4)"
                      : "rgba(25,103,210,0.3)",
                  color: theme.palette.mode === "dark" ? "#8ab4f8" : "#1967d2",
                  "&:hover": {
                    borderColor:
                      theme.palette.mode === "dark" ? "#8ab4f8" : "#1967d2",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(138,180,248,0.08)"
                        : "rgba(25,103,210,0.06)",
                  },
                }}
              />
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
}
